import { Module } from "../lib/modules";
import { StateManager } from "../lib/stateManager";
import sleep from "../utils/sleep";

interface ArticlesState {
  likes: Set<string>;
  offset: number;
  searchQuery: string;
  sortBy: string;
  selectedTags: string[];
}

export const Articles: Module<ArticlesState> = () => {
  // Create a state manager with initial state
  const state = new StateManager<ArticlesState>({
    likes: new Set<string>(),
    offset: 0,
    searchQuery: "",
    sortBy: "date_desc",
    selectedTags: []
  });

  const toggleLike = (slug: string) => {
    const likes = state.get("likes");
    if (likes.has(slug)) {
      likes.delete(slug);
    } else {
      likes.add(slug);
    }
    state.set("likes", likes);
  };

  // Handle like button click
  const handleLikeClick = (slug: string) => {
    console.log("Like button clicked for slug:", slug);
    toggleLike(slug);
  };

  const handleCopyClick = async (slug: string, x: number, y: number) => {
    // Await so that the copy text is shown after allowing clipboard write
    await window.navigator.clipboard.writeText("https://videogamesmademe.com/article/" + slug);

    const copyText = document.createElement("span");
    copyText.innerText = "Copied!";
    copyText.classList.add("articles__copy-text");
    copyText.style.top = `${y}px`;
    copyText.style.left = `${x}px`;

    document.body.appendChild(copyText);

    await sleep(10);
    copyText.style.top = `${y - 130}px`;
    copyText.style.opacity = "0";
    await sleep(2000); // Should be as long as the CSS transition
    copyText.remove();
  };

  // Load more articles
  const handleLoadMore = async () => {
    try {
      const loadMoreBtn = document.querySelector(".js-load-more-btn") as HTMLButtonElement;
      if (loadMoreBtn) {
        loadMoreBtn.disabled = true;
        loadMoreBtn.textContent = "Loading...";
      }

      // Build the query parameters
      const queryParams = new URLSearchParams();
      queryParams.append("offset", state.get("offset").toString());
      queryParams.append("limit", "20");

      const searchQuery = state.get("searchQuery");
      if (searchQuery) {
        queryParams.append("search", searchQuery);
      }

      queryParams.append("sort", state.get("sortBy"));

      const selectedTags = state.get("selectedTags");
      if (selectedTags.length > 0) {
        queryParams.append("tags", selectedTags.join(","));
      }

      const res = await fetch(`/api/articles/more?${queryParams.toString()}`);

      if (!res.ok) {
        throw new Error("Failed to load more articles");
      }

      const data = await res.text();

      const articlesContainer = document.querySelector(".articles__list") as HTMLDivElement;

      const parser = new DOMParser();
      const doc = parser.parseFromString(data, "text/html");
      const newArticles = doc.querySelectorAll(".articles__card");

      for (const article of newArticles) {
        const articleCard = article as HTMLDivElement;
        articlesContainer.appendChild(articleCard);
      }

      // Update offset
      const newOffset = state.get("offset") + newArticles.length;
      state.set("offset", newOffset);

      // If no more articles were returned, hide the load more button
      if (newArticles.length === 0) {
        const loadMoreContainer = document.querySelector(".articles__load-more");
        if (loadMoreContainer) {
          loadMoreContainer.classList.add("articles__load-more--hidden");
        }
      }

      // Re-enable the button
      if (loadMoreBtn) {
        loadMoreBtn.disabled = false;
        loadMoreBtn.textContent = "Load More";
      }

      // Initialize the like buttons for new articles
      initArticleCards();
    } catch (error) {
      console.error("Error loading more articles:", error);

      // Re-enable the button on error
      const loadMoreBtn = document.querySelector(".js-load-more-btn") as HTMLButtonElement;
      if (loadMoreBtn) {
        loadMoreBtn.disabled = false;
        loadMoreBtn.textContent = "Load More";
      }

      // Show an error message
      const errorEl = document.querySelector(".articles__error-state");
      if (errorEl) {
        errorEl.textContent = "Failed to load more articles. Please try again.";
        errorEl.classList.add("show");
      }
    }
  };

  const handleSearch = async (e?: Event) => {
    if (e) {
      e.preventDefault();
    }

    try {
      // Show loading state
      const articlesContainer = document.querySelector(".articles__list") as HTMLDivElement;
      if (articlesContainer) {
        articlesContainer.innerHTML = '<div class="articles__loading-state">Loading articles...</div>';
      }

      // Reset offset
      state.set("offset", 0);

      // Get the current search input
      const searchInput = document.querySelector(".js-search-input") as HTMLInputElement;
      if (searchInput) {
        state.set("searchQuery", searchInput.value);
      }

      // Build the query parameters
      const queryParams = new URLSearchParams();
      queryParams.append("offset", "0");
      queryParams.append("limit", "20");

      const searchQuery = state.get("searchQuery");
      if (searchQuery) {
        queryParams.append("search", searchQuery);
      }

      queryParams.append("sort", state.get("sortBy"));

      const selectedTags = state.get("selectedTags");
      if (selectedTags.length > 0) {
        queryParams.append("tags", selectedTags.join(","));
      }

      // Update URL without page refresh
      const newUrl = `/articles?${queryParams.toString()}`;
      window.history.replaceState({}, "", newUrl);

      const res = await fetch(`/api/articles/search?${queryParams.toString()}`);

      if (!res.ok) {
        throw new Error("Failed to search articles");
      }

      const data = await res.text();

      if (articlesContainer) {
        articlesContainer.innerHTML = data;
      }

      // Update the article count for pagination
      const articleCards = document.querySelectorAll(".articles__card");
      state.set("offset", articleCards.length);

      // Show/hide the load more button based on the number of results
      const loadMoreContainer = document.querySelector(".articles__load-more");
      if (loadMoreContainer) {
        if (articleCards.length === 0 || articleCards.length < 10) {
          loadMoreContainer.classList.add("articles__load-more--hidden");
        } else {
          loadMoreContainer.classList.remove("articles__load-more--hidden");
        }
      }

      // Initialize the like buttons for new articles
      initArticleCards();
    } catch (error) {
      console.error("Error searching articles:", error);

      // Show an error message
      const articlesContainer = document.querySelector(".articles__list") as HTMLDivElement;
      if (articlesContainer) {
        articlesContainer.innerHTML =
          '<div class="articles__error-state">Failed to search articles. Please try again.</div>';
      }
    }
  };

  const handleSortChange = (e: Event) => {
    const select = e.target as HTMLSelectElement;
    state.set("sortBy", select.value);
    handleSearch();
  };

  const handleTagToggle = (tagId: string, tagElement: HTMLElement) => {
    const selectedTags = state.get("selectedTags");
    const tagIndex = selectedTags.indexOf(tagId);

    // Toggle the tag selection in the array
    if (tagIndex > -1) {
      // Remove tag if already selected
      selectedTags.splice(tagIndex, 1);

      // Update visual state of the tag element
      tagElement.classList.remove("articles__tag--selected");

      // Remove the close icon if it exists
      const closeIcon = tagElement.querySelector(".articles__tag--close-icon");
      if (closeIcon) {
        closeIcon.remove();
      }
    } else {
      // Add tag if not selected
      selectedTags.push(tagId);

      // Update visual state of the tag element
      tagElement.classList.add("articles__tag--selected");

      // Add the close icon
      const closeIcon = document.createElement("span");
      closeIcon.className = "articles__tag--close-icon";
      closeIcon.textContent = "×";
      tagElement.appendChild(closeIcon);
    }

    // Update the state
    state.set("selectedTags", selectedTags);

    // Perform the search with the updated tags
    handleSearch();
  };

  // Initialize article cards with event listeners
  const initArticleCards = () => {
    const articleCards = document.querySelectorAll(".articles__card");
    const likes = state.get("likes");

    for (const c of articleCards) {
      const card = c as HTMLDivElement;

      // Like button
      const likeButton = card.querySelector(".articles__like") as HTMLButtonElement;
      if (!likeButton) continue;

      // Check if already liked
      const cardId = card.dataset.id || "";
      if (likes.has(cardId)) {
        likeButton.classList.add("articles__like--active");
      } else {
        likeButton.classList.remove("articles__like--active");
      }

      // Remove existing event listeners (to prevent duplicates)
      const newLikeButton = likeButton.cloneNode(true);
      likeButton.parentNode?.replaceChild(newLikeButton, likeButton);

      // Add new event listener
      newLikeButton.addEventListener("click", () => {
        handleLikeClick(cardId);
      });

      // Copy button
      const copyButton = card.querySelector(".articles__share") as HTMLButtonElement;
      if (!copyButton) continue;

      // Remove existing event listeners
      const newCopyButton = copyButton.cloneNode(true);
      copyButton.parentNode?.replaceChild(newCopyButton, copyButton);

      // Add new event listener
      newCopyButton.addEventListener("click", (e) => {
        // Get the scroll position
        const scrollX = window.pageXOffset || document.documentElement.scrollLeft;
        const scrollY = window.pageYOffset || document.documentElement.scrollTop;

        // Position the element at the click coordinates, adjusted for scroll
        const evt = e as MouseEvent;
        const x = evt.clientX + scrollX;
        const y = evt.clientY + scrollY;

        handleCopyClick(cardId, x, y);
      });
    }
  };

  // Initialize tag filters
  const initTagFilters = () => {
    const tags = document.querySelectorAll(".articles__tag");

    tags.forEach((tag) => {
      // Remove any existing event listeners
      const newTag = tag.cloneNode(true);
      tag.parentNode?.replaceChild(newTag, tag);

      // Add new event listener
      newTag.addEventListener("click", () => {
        const tagElement = newTag as HTMLElement;
        const tagId = tagElement.dataset.id || "";
        handleTagToggle(tagId, tagElement);
      });
    });

    console.log("Tag filters initialized with", tags.length, "tags");
  };

  const attachEventListeners = () => {
    // Load more button
    const loadMoreButton = document.querySelector(".js-load-more-btn") as HTMLButtonElement;

    if (loadMoreButton) {
      loadMoreButton.addEventListener("click", handleLoadMore);
    }

    // Search form
    const searchForm = document.querySelector(".js-search-form") as HTMLFormElement;

    if (searchForm) {
      searchForm.addEventListener("submit", handleSearch);
    }

    // Sort dropdown
    const sortSelect = document.querySelector(".js-sort-select") as HTMLSelectElement;

    if (sortSelect) {
      sortSelect.addEventListener("change", handleSortChange);
    }

    // Initialize tag filters
    initTagFilters();
  };

  const detachEventListeners = () => {
    // Load more button
    const loadMoreButton = document.querySelector(".js-load-more-btn") as HTMLButtonElement;

    if (loadMoreButton) {
      loadMoreButton.removeEventListener("click", handleLoadMore);
    }

    // Search form
    const searchForm = document.querySelector(".js-search-form") as HTMLFormElement;

    if (searchForm) {
      searchForm.removeEventListener("submit", handleSearch);
    }

    // Sort dropdown
    const sortSelect = document.querySelector(".js-sort-select") as HTMLSelectElement;

    if (sortSelect) {
      sortSelect.removeEventListener("change", handleSortChange);
    }
  };

  const init = () => {
    // Initialize like/share buttons on article cards
    initArticleCards();

    // Attach event listeners
    attachEventListeners();

    // Set offset based on current article count
    state.set("offset", document.querySelectorAll(".articles__card").length);

    // Load likes from localStorage
    const likes = window.localStorage.getItem("likes");
    if (likes) {
      const parsedLikes = JSON.parse(likes) as string[];
      state.set("likes", new Set(parsedLikes));
    }

    // Initialize selectedTags from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const tagsParam = urlParams.get("tags");
    if (tagsParam) {
      const selectedTagIds = tagsParam.split(",").filter((tag) => tag.trim() !== "");
      state.set("selectedTags", selectedTagIds);
    }
  };

  const destroy = () => {
    detachEventListeners();
  };

  // Set up state change observers
  state.onChange("likes", (newState) => {
    window.localStorage.setItem("likes", JSON.stringify(Array.from(newState)));
    const articleCards = document.querySelectorAll(".articles__card");
    for (const c of articleCards) {
      const card = c as HTMLDivElement;
      const likeButton = card.querySelector(".articles__like") as HTMLButtonElement;
      if (!likeButton) continue;
      const slug = card.dataset.id as string;
      if (newState.has(slug)) {
        likeButton.classList.add("articles__like--active");
      } else {
        likeButton.classList.remove("articles__like--active");
      }
    }
  });

  return {
    init,
    destroy,
    state,
    pages: ["/articles"],
    name: "articles"
  };
};
